import { Link } from "react-router-dom";
import { useFormik } from "formik";
import {
  Dialog,
  Button,
  TextInput,
  KeyIcon,
  MailIcon,
  ProfileIcon,
  Typography,
  theme,
} from "kosmos-ui/lib";

import { useSignUpShopperMutation } from "api/graphql/mutations/shopper/hooks";
import { useBackgroundLocation } from "app/hooks/routes";
import { useLayout } from "app/context/LayoutContext/hooks";
import { ROUTES } from "app/constants/app";

import { signUpShopperFormValidationSchema } from "./helpers";
import { TShopperSignUpForm } from "./types";
import { GroupedInputsContainer, NotesContainer } from "./styles";

export default function ShopperSignUp() {
  const { mobile } = useLayout();
  const { location, goBackToBackgroundLocation } = useBackgroundLocation();

  const [signUpShopperMutation, { loading: loadingSignUpShopperMutation }] =
    useSignUpShopperMutation({
      onCompleted() {
        goBackToBackgroundLocation();
      },
    });

  const form = useFormik<TShopperSignUpForm>({
    initialValues: { firstname: "", lastname: "", email: "", password: "" },
    validationSchema: signUpShopperFormValidationSchema,
    onSubmit(values) {
      signUpShopperMutation({ variables: values });
    },
  });

  return (
    <Dialog
      show
      style={{
        width: mobile ? "75vw" : "33vw",
        maxWidth: 560,
        backgroundColor: theme.colorsPalette.black.default,
      }}
    >
      <Dialog.Header>
        <Dialog.Title style={{ color: theme.colorsPalette.white.default }}>
          Registrate en Grafito
        </Dialog.Title>

        <Dialog.Close
          onClick={goBackToBackgroundLocation}
          style={{ backgroundColor: "transparent" }}
        />
      </Dialog.Header>

      <Dialog.Body>
        <NotesContainer>
          Registrandote en Grafito podrás comprar más rápidamente y descubrir
          nuevos productos y categorías de tu interés.
        </NotesContainer>

        <GroupedInputsContainer>
          <TextInput
            name="firstname"
            value={form.values.firstname}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            label="Nombre*"
            variant="filled"
            adornments={{ left: <ProfileIcon height="20px" /> }}
            error={!!form.errors.firstname && form.touched.firstname}
            style={{
              root: {
                marginRight: 16,
              },
              inputContainer: {
                backgroundColor: theme.colorsPalette.black.lighter,
                border: "none",
              },
            }}
            colors={{ base: theme.colorsPalette.gray.default }}
          />

          <TextInput
            name="lastname"
            value={form.values.lastname}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            label="Apellido*"
            variant="filled"
            adornments={{ left: <ProfileIcon height="20px" /> }}
            error={!!form.errors.lastname && form.touched.lastname}
            style={{
              inputContainer: {
                backgroundColor: theme.colorsPalette.black.lighter,
                border: "none",
              },
            }}
            colors={{ base: theme.colorsPalette.gray.default }}
          />
        </GroupedInputsContainer>

        <TextInput
          name="email"
          value={form.values.email}
          onChange={form.handleChange}
          onBlur={form.handleBlur}
          label="Email*"
          variant="filled"
          adornments={{ left: <MailIcon height="20px" /> }}
          error={!!form.errors.email && form.touched.email}
          style={{
            root: {
              marginBottom: 8,
            },
            inputContainer: {
              backgroundColor: theme.colorsPalette.black.lighter,
              border: "none",
            },
          }}
          colors={{ base: theme.colorsPalette.gray.default }}
        />

        <TextInput
          name="password"
          type="password"
          value={form.values.password}
          onChange={form.handleChange}
          onBlur={form.handleBlur}
          label="Contraseña*"
          variant="filled"
          adornments={{ left: <KeyIcon height="20px" /> }}
          error={!!form.errors.password && form.touched.password}
          style={{
            inputContainer: {
              backgroundColor: theme.colorsPalette.black.lighter,
              border: "none",
            },
          }}
        />
      </Dialog.Body>

      <Dialog.Footer style={{ flexDirection: "column", alignItems: "center" }}>
        <Button
          variant="primary"
          onClick={() => form.handleSubmit()}
          loading={loadingSignUpShopperMutation}
          style={{ width: "100%", marginBottom: 8 }}
        >
          Crear cuenta
        </Button>

        <Typography as="p" variant="paragraph3">
          ¿Ya tienes una cuenta? Inicia sesión{" "}
          <Link
            to={{
              pathname: ROUTES.shopperSignIn,
              state: location.state,
            }}
            replace
            style={{
              textDecoration: "none",
              color: theme.colorsPalette.blue.default,
            }}
          >
            aquí
          </Link>
          .
        </Typography>
      </Dialog.Footer>
    </Dialog>
  );
}
